// hooks
import { Fragment, useEffect, useState, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import useHttp from "hooks/use-http";

import { MdDelete, MdEdit } from "react-icons/md";

// material styles
import {
  Grid,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";

// material icons
import { FilterAlt, Delete, Download } from "@mui/icons-material";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import DatePickerCM from "components/shared/DatePicker";
import TableCM from "components/shared/Table";
import PaginationCM from "components/shared/Pagination";
import DeleteModal from "components/shared/Modal/Delete";

// helper
import GenerateHeadCell from "helper/generate-head-cell";
import spliter from "helper/digit-spliter";
import Toast from "helper/toast";

// styled component
import {
  StyledStack,
  StyledDateBox,
  StyledMenuItem,
  StyledGridIcons,
} from "./excellScores.style";

// request methods
import {
  getEducationYearRequest,
  getStudentScoresExcellFileRequest,
} from "api/requests";

// constants
import { PAGINATION_LIMIT } from "constants";

import { useNavigate } from "react-router-dom";
import { convertNum } from "helper/convert-num";
import { getStudentClassesRequest } from "../../../api/requests";
import { RiNumbersFill } from "react-icons/ri";

// excell scores head cells
const headCells = GenerateHeadCell("excellScores_list");

export default function DisplayExcellScores() {
  const {
    register,
    control,
    formState: { errors },
  } = useForm();

  const navigate = useNavigate();

  const { request, loading, setLoading } = useHttp(false);

  // states for filtered
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedEducationalYear, setSelectedEducationalYear] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  // excellScores data state
  const [excellScoresData, setExcellScoresData] = useState({
    data: [],
    count: 0,
  });

  // education years data state
  const [educationalYears, setEducationalYears] = useState([]);
  const [classesData, setClassesData] = useState({ data: [], count: 0 });
  // page state for pagination
  const [page, setPage] = useState(1);

  // date filter handler
  const onFromDateHandler = (value) =>
    setFromDate(
      value
        .format("YYYY-MM-DD")
        .split("-")
        .map((item) => convertNum(item))
        .join("-")
    );

  const onToDateHandler = (value) =>
    setToDate(
      value
        .format("YYYY-MM-DD")
        .split("-")
        .map((item) => convertNum(item))
        .join("-")
    );

  // get all items
  const getExcellScoresList = async () => {
    setLoading(true);
    const educationYearsRes = await request({
      url: getEducationYearRequest,
      isMulty: true,
    });

    if (educationYearsRes) {
      setEducationalYears(educationYearsRes);
      setSelectedEducationalYear({
        value: educationYearsRes[educationYearsRes.length - 2],
      });
    }
    const classRes = await request({
      url: getStudentClassesRequest,
      isMulty: true,
    });
    if (classRes) {
      setClassesData({ data: classRes.data.data, count: classRes.data.count });
    }

    const values = {
      skip: (page - 1) * PAGINATION_LIMIT,
      limit: PAGINATION_LIMIT,
      search: searchValue ?? null,
      class_id: selectedClass ?? null,
      educational_year: selectedEducationalYear.value ?? null,
      from_date: fromDate ?? null,
      to_date: toDate ?? null,
    };

    !values.class_id && delete values.class_id;
    !searchValue.length && delete values.search;
    !values.from_date && delete values.from_date;
    !values.to_date && delete values.to_date;

    const res = await request({
      url: getStudentScoresExcellFileRequest,
      values,
    });

    if (res) {
      if (res?.count < 1) {
        return setExcellScoresData({
          count: res?.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد." }],
        });
      }
      let transformedData = res?.data?.map((items) => ({
        id: items.id,
        score_title: items?.title ?? "ثبت نشد",
        score_date: items?.date?.string,
        excell_file: (
          <Tooltip title="نمایش" placement="top">
            <IconButton
              style={{ color: "#ffb74d" }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/user_scores_excell-file/${items.id}`);
              }}
            >
              <RiNumbersFill />
            </IconButton>
          </Tooltip>
        ),
      }));

      setExcellScoresData({
        data: transformedData,
        count: res?.count,
      });
    }
    setLoading(false);
  };
  // get items without filter
  const getExcellScoresListWithoutFiltered = async () => {
    setLoading(true);

    setFromDate("");
    setToDate("");
    setSearchValue("");
    setSelectedClass("");
    setSelectedEducationalYear("");

    const educationYearsRes = await request({
      url: getEducationYearRequest,
      isMulty: true,
    });

    if (educationYearsRes) {
      setEducationalYears(educationYearsRes);
      setSelectedEducationalYear({
        value: educationYearsRes[educationYearsRes.length - 2],
      });
    }
    const classRes = await request({
      url: getStudentClassesRequest,
      isMulty: true,
    });
    if (classRes) {
      setClassesData({ data: classRes.data.data, count: classRes.data.count });
    }

    const res = await request({
      url: getStudentScoresExcellFileRequest,
      values: {
        skip: (page - 1) * PAGINATION_LIMIT,
        limit: PAGINATION_LIMIT,
      },
    });

    if (res) {
      if (res?.count < 1) {
        return setExcellScoresData({
          count: res?.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد." }],
        });
      }
      let transformedData = res?.data?.map((items) => ({
        id: items.id,
        score_title: items?.title ?? "ثبت نشد",
        score_date: items?.date?.string,
        excell_file: (
          <Tooltip title="نمایش" placement="top">
            <IconButton
              style={{ color: "#ffb74d" }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/user_scores_excell-file/${items.id}`);
              }}
            >
              <Download />
            </IconButton>
          </Tooltip>
        ),
      }));

      setExcellScoresData({
        data: transformedData,
        count: res?.count,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    getExcellScoresList();
  }, [page]);

  return (
    <DrawerCM title="لیست نمرات " $styledHeader>
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <StyledStack>
            <Grid container>
              <Grid item sm={4} xs={12} p={0.5}>
                <TextField
                  label="جست و جو"
                  fullWidth
                  id="outlined-size-small"
                  size="small"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </Grid>
              <Grid item sm={4} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select_educationalYear"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select-educational-year">
                        انتخاب سال تحصیلی
                      </InputLabel>
                      <Select
                        id="educationalYear"
                        name="educationalYear"
                        label="انتخاب سال تحصیلی"
                        labelId="select-educational-year"
                        value={selectedEducationalYear?.value ?? ""}
                        {...register("educationalYear")}
                        error={Boolean(errors.educationalYear)}
                        helperText={errors.educationalYear?.message}
                        onChange={(e) =>
                          setSelectedEducationalYear({
                            value: e.target.value,
                          })
                        }
                      >
                        <StyledMenuItem value={"*"}>{"همه"}</StyledMenuItem>
                        {educationalYears.map((item, index) => {
                          return (
                            <StyledMenuItem key={index} value={item}>
                              {item + 1} - {item}
                            </StyledMenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item sm={4} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select_class"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select-class">انتخاب کلاس</InputLabel>
                      <Select
                        id="class"
                        name="class"
                        label="انتخاب کلاس"
                        value={selectedClass ?? ""}
                        {...register("class_id")}
                        error={Boolean(errors.class_id)}
                        helperText={errors.class_id?.message}
                        onChange={(e) => setSelectedClass(e.target.value)}
                      >
                        {classesData?.data?.map((item, index) => (
                          <StyledMenuItem key={index} value={item.id}>
                            {item.title}
                          </StyledMenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item sm={5} xs={12} p={0.5}>
                <StyledDateBox>
                  <DatePickerCM
                    onChange={onFromDateHandler}
                    render={(value, openCalendar) => {
                      return (
                        <TextField
                          label="از تاریخ"
                          variant="outlined"
                          fullWidth
                          size="small"
                          id="from_created_at"
                          {...register("from_created_at")}
                          error={Boolean(errors.from_created_at)}
                          helperText={errors.from_created_at?.message}
                          value={fromDate ? fromDate.split("-").join("/") : ""}
                          onClick={openCalendar}
                        />
                      );
                    }}
                  />
                </StyledDateBox>
              </Grid>
              <Grid item sm={5} xs={12} p={0.5}>
                <StyledDateBox>
                  <DatePickerCM
                    onChange={onToDateHandler}
                    render={(value, openCalendar) => {
                      return (
                        <TextField
                          label="تا تاریخ"
                          variant="outlined"
                          fullWidth
                          size="small"
                          id="to_created_at"
                          {...register("to_created_at")}
                          error={Boolean(errors.to_created_at)}
                          helperText={errors.to_created_at?.message}
                          value={toDate ? toDate.split("-").join("/") : ""}
                          onClick={openCalendar}
                        />
                      );
                    }}
                  />
                </StyledDateBox>
              </Grid>
              <StyledGridIcons item sm={2} xs={12} p={0.5}>
                <IconButton color="primary" onClick={getExcellScoresList}>
                  <Tooltip title="اعمال فیلتر" placement="top">
                    <FilterAlt />
                  </Tooltip>
                </IconButton>
                <IconButton
                  color="error"
                  onClick={getExcellScoresListWithoutFiltered}
                >
                  <Tooltip title="حذف فیلتر" placement="top">
                    <Delete />
                  </Tooltip>
                </IconButton>
              </StyledGridIcons>
            </Grid>
          </StyledStack>
          <Grid container sx={{ display: "flex", justifyContent: "center" }}>
            <Grid item xs={12} my={2}>
              <TableCM
                title="لیست نمرات "
                rows={excellScoresData.data}
                headCells={headCells}
                page={page}
                colspan={excellScoresData.count === 0 ? "8" : ""}
                fontSize="14px"
                cellFontSize="14px"
              />
            </Grid>
            {excellScoresData.data.length < 1 ? (
              ""
            ) : (
              <PaginationCM
                handleChange={(_, val) => setPage(val)}
                page={page}
                count={excellScoresData.count}
              />
            )}
          </Grid>
        </Fragment>
      )}
    </DrawerCM>
  );
}
